import { memo, useCallback, useMemo, useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import dayjs from 'dayjs';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { RhfValidators } from 'core/forms';
import { useEvent, useInterval, useQueryParam, useValidatedParam } from 'core/hooks';
import { useInitializationEffect } from 'core/hooks/use-initialization-effect';
import { BreakpointSelectors, Button, Card, CheckboxField, InputField, MessageBox, PAGE_GRID_CLASSES, Page } from 'core/ui';
import { hasText } from 'core/utils';

import { useQueryStudySharePublic } from 'features/api/hooks/useQueryStudySharePublic';
import { useAuthentication, useCurrentUser } from 'features/auth';
import { AuthenticationScheme } from 'features/auth/constants';

import { ShareAuthenticationErrorCode } from '../constants';
import { ShareService } from '../services';
import compumedLogo from './compumed_logo_blue.webp';

const consentValidator = RhfValidators.requireValue(true, 'HIPAA agreement not accepted.');

type AuthenticateFormValues = {
  accessCode?: string;
  consent: boolean;
  // Optional user information
  email?: string;
};

export const ShareLanding = memo(() => {
  const navigate = useNavigate();
  const { loginShare, activeScheme } = useAuthentication();
  const { currentUser } = useCurrentUser();

  const linkId = useValidatedParam('linkId', 'guid', true);
  const returnRoute = useQueryParam('return', true);

  const { data: share, promise: sharePromise, isPending: isShareQueryPending, isError: isShareQueryError } = useQueryStudySharePublic(linkId);
  const [errors, setErrors] = useState<string[]>([]);
  const [now, setNow] = useState(() => dayjs.utc().tz('America/Los_Angeles'));
  const [autoLoginState, setAutoLoginState] = useState<'pending' | 'success' | 'error' | 'not-attempted'>('pending');

  const oidcAutoLogin = useCallback(async () => {
    // Just throw if required dependencies are not met.  Because the useInitializationEffect must also reference them since it has a dependency list for the internal useEffect.
    if (activeScheme !== AuthenticationScheme.OIDC) throw new Error('The active scheme is not OIDC.');
    if (currentUser === 'anonymous') throw new Error('The currentUser is "anonymous" but OIDC is the active scheme.');
    if (currentUser == null) throw new Error('The currentUser is null or undefined.');

    try {
      const isDirectUploadAccess = location.pathname.includes('/upload');
      const userClaims = {
        email: currentUser.email,
        name: `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email,
      };

      const result = await loginShare(linkId, null, true, userClaims);

      if (result.success) {
        // Prefer returnRoute, then /upload if direct upload, else default
        if (hasText(returnRoute)) {
          navigate(returnRoute);
        } else if (isDirectUploadAccess) {
          navigate(`/share/${linkId}/upload`);
        } else {
          navigate(`/share/${linkId}`);
        }

        setAutoLoginState('success');
      } else {
        // Auto-login failed - set error state and show manual login form
        datadogLogs.logger.error('Share auto-login failed.', {
          errorCode: result.errorCode,
          errorMessage: result.errorMessage,
        });

        // Set appropriate error message based on error code
        let errorMessage: string;
        switch (result.errorCode) {
          case ShareAuthenticationErrorCode.ShareExpired:
            errorMessage = 'This share has expired and is no longer accessible.';
            break;
          case ShareAuthenticationErrorCode.ShareNotFound:
            errorMessage = 'Share was not found.';
            break;
          default:
            errorMessage = 'Unable to access share automatically. Please try logging in manually below.';
        }

        setAutoLoginState('error');
        setErrors([errorMessage]);
      }
    } catch (error: unknown) {
      datadogLogs.logger.error(
        'Unhandled share auto-login failure.',
        error instanceof Error
          ? undefined
          : {
              error,
            },
        error instanceof Error ? error : undefined,
      );

      setAutoLoginState('error');
      setErrors(['An error occurred while trying to access the share. Please try again.']);
    }
  }, [activeScheme, currentUser, linkId, loginShare, navigate, returnRoute]);

  const bypassSplashAutoLogin = useCallback(async () => {
    await loginShare(linkId, null, true);
  }, [linkId, loginShare]);

  useInitializationEffect(
    (setIsInitialized) => {
      if (activeScheme === AuthenticationScheme.OIDC) {
        if (currentUser == null) return;

        // Signal that the initialization effect has been executed.  This needs to be done before the auto login is attempted to avoid double execution since the auto login is an async operation.
        setIsInitialized();
        oidcAutoLogin();
      } else if (activeScheme === AuthenticationScheme.ANONYMOUS) {
        if (share == null) return;

        // Signal that the initialization effect has been executed.  This needs to be done before the auto login is attempted to avoid double execution since the auto login is an async operation.
        setIsInitialized();

        if (share.byPassSplashPage) {
          bypassSplashAutoLogin();
        } else {
          setAutoLoginState('not-attempted');
        }
      }
    },
    [activeScheme, bypassSplashAutoLogin, currentUser, oidcAutoLogin, share],
  );

  const rhfContext = useForm<AuthenticateFormValues>({
    defaultValues: async () => {
      const share = await sharePromise;
      return {
        ...(share?.hasPassword ? { accessCode: '' } : {}),
        consent: false,
        email: '',
      };
    },
  });

  const shareDate = useMemo(() => {
    if (share?.dateCreated == null) return null;
    return dayjs.utc(share.dateCreated).tz('America/Los_Angeles');
  }, [share?.dateCreated]);

  const expiration = useMemo(() => {
    if (share?.expireOn == null) return null;
    return dayjs.utc(share.expireOn).tz('America/Los_Angeles');
  }, [share]);

  const expireDuration = useMemo(() => {
    if (expiration == null) return null;
    const newDuration = dayjs.duration(expiration.diff(now));
    return newDuration;
  }, [expiration, now]);

  // Check if share is expired
  const isShareExpired = useMemo(() => {
    if (expiration == null) return false;
    return expiration.diff(now) <= 0;
  }, [expiration, now]);

  const handleSubmit: SubmitHandler<AuthenticateFormValues> = useEvent(async (values) => {
    if (share == null) throw new Error('Submit cannot be called until the share information has been retrieved.');

    // Clear previous errors
    setErrors([]);

    // Build userClaims if email is provided
    let userClaims: Record<string, string> | undefined;
    // Email field is currently hidden
    // if (hasText(values.email)) {
    //   userClaims = {
    //     email: values.email.trim(),
    //   };
    // }

    const result = await loginShare(linkId, typeof values.accessCode !== 'undefined' ? values.accessCode : null, values.consent, userClaims);

    if (result.success) {
      navigate(hasText(returnRoute) ? returnRoute : `/share/${linkId}`);
    } else {
      let newErrorMessage: string;
      switch (result.errorCode) {
        case ShareAuthenticationErrorCode.HipaaConsentNotAccepted:
          newErrorMessage = 'HIPAA agreement not accepted.';
          break;
        case ShareAuthenticationErrorCode.ShareNotFound:
          newErrorMessage = 'Share was not found.';
          break;
        case ShareAuthenticationErrorCode.ShareExpired:
          newErrorMessage = 'Share has expired.';
          break;
        case ShareAuthenticationErrorCode.PasswordValidationFailed:
          newErrorMessage = 'Incorrect access code.';
          break;
        default:
          newErrorMessage = 'Unable to login for share.';
      }
      setErrors([newErrorMessage]);
    }
  });

  // Update the value for "now" every second to ensure that the duration is always up to date.
  useInterval(() => setNow(dayjs.utc().tz('America/Los_Angeles')), 1000);

  // Show spinner while auto-login is in progress or should auto-login
  if (isShareQueryPending || autoLoginState === 'pending') {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <span>Loading...</span>
        </div>
      </Page>
    );
  }

  // Show loading for OIDC users who will auto-login (prevent HIPAA form flicker)
  if (activeScheme === AuthenticationScheme.OIDC && currentUser !== 'anonymous' && autoLoginState !== 'error' && autoLoginState !== 'not-attempted') {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <span>Authenticating...</span>
        </div>
      </Page>
    );
  }

  // Show loading for anonymous users with byPass splash page (prevent HIPAA form flicker)
  if (activeScheme === AuthenticationScheme.ANONYMOUS && share?.byPassSplashPage && autoLoginState !== 'error' && autoLoginState !== 'not-attempted') {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <span>Loading...</span>
        </div>
      </Page>
    );
  }

  if (isShareQueryError) {
    throw new Error('Error loading share.');
  } else if (share == null) {
    return (
      <Page>
        <StyledErrorContainer>
          <StyledErrorCard>
            <h2>Share Not Found</h2>
            <p>This share no longer exists or has been removed.</p>
            <p>Please contact the person who shared this link with you for assistance.</p>
          </StyledErrorCard>
        </StyledErrorContainer>
      </Page>
    );
  }

  // Show error page for expired shares
  if (isShareExpired) {
    return (
      <Page>
        <StyledErrorContainer>
          <StyledErrorCard>
            <StyledErrorLogoContainer>
              <img src={compumedLogo} alt="CompuMed Logo" />
            </StyledErrorLogoContainer>
            <h2>Share Expired</h2>
            <p>This share has expired and is no longer accessible.</p>
            <p>
              Share expired on: <strong>{expiration?.format('YYYY-MM-DD HH:mm:ss')} PDT</strong>
            </p>
            <p>Please contact the person who shared this link with you to request a new share.</p>
          </StyledErrorCard>
        </StyledErrorContainer>
      </Page>
    );
  }

  // Show error page for auto-login failures that indicate the share is inaccessible
  if (
    autoLoginState === 'error' &&
    errors.some((error) => error.includes('expired') || error.includes('not found') || error.includes('no longer accessible'))
  ) {
    return (
      <Page>
        <StyledErrorContainer>
          <StyledErrorCard>
            <StyledErrorLogoContainer>
              <img src={compumedLogo} alt="CompuMed Logo" />
            </StyledErrorLogoContainer>
            <h2>Share Unavailable</h2>
            {errors.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
            <p>Please contact the person who shared this link with you for assistance.</p>
          </StyledErrorCard>
        </StyledErrorContainer>
      </Page>
    );
  }

  // Show an empty page if the auto login was successful.  This is to avoid the form from being rendered when the auto login is successful.  This won't actually render to the DOM because the auto login will have already navigated to the appropriate page.
  if (autoLoginState === 'success') {
    return <Page />;
  }

  return (
    <Page>
      <FormProvider {...rhfContext}>
        <StyledShareHeader className={PAGE_GRID_CLASSES.Flush}>
          <StyledInfoList>
            {shareDate != null && (
              <StyledInfoContainer>
                <StyledInfoLabel>SHARE DATE:</StyledInfoLabel>
                <StyledInfoValue>{shareDate.format('YYYY-MM-DD HH:mm:ss')} PT</StyledInfoValue>
              </StyledInfoContainer>
            )}

            {expireDuration != null && (
              <StyledInfoContainer>
                <StyledInfoLabel>EXPIRATION:</StyledInfoLabel>
                <StyledInfoValue>{ShareService.formatExpireMessage(expiration, expireDuration)}</StyledInfoValue>
              </StyledInfoContainer>
            )}
          </StyledInfoList>

          <StyledLogoContainer>
            <img src={compumedLogo} alt="CompuMed Logo" />
          </StyledLogoContainer>
        </StyledShareHeader>

        <StyledPageContent>
          <StyledForm autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck="false" noValidate onSubmit={rhfContext.handleSubmit(handleSubmit)}>
            <StyledCard>
              <h2>Welcome to CompuMed</h2>
              <hr />
              <p>Welcome to the CompuMed image sharing application.</p>

              {(share?.senderName || share?.senderEmail || share?.description) && (
                <p>
                  {share.senderName && (
                    <span>
                      Shared by: {share.senderName}
                      <br />
                    </span>
                  )}
                  {share.senderEmail && (
                    <span>
                      Email: {share.senderEmail}
                      <br />
                    </span>
                  )}
                  {share.description && <span>Description: {share.description}</span>}
                </p>
              )}

              <p>
                {share.hasPassword ? (
                  <>To continue, please enter your access code and read and accept the HIPAA Privacy Policy Notice:</>
                ) : (
                  <>To continue, please read and accept the HIPAA Privacy Policy Notice:</>
                )}
              </p>
            </StyledCard>

            <StyledCard>
              <h2>HIPAA AGREEMENT</h2>
              <hr />
              <p>PHI Usage Through the Use of CompuMed, Inc. Imaging Products</p>
              <p>
                This Health Insurance Portability and Accountability Act (HIPAA) Research Agreement (HIPAA Agreement) is made by and between you, the user, and
                CompuMed, Inc. Imaging, Inc.
              </p>
              <p>
                HIPAA sets forth a rule (Privacy Rule) governing the privacy of a patient&apos;s identifiable health information (referred to in the Privacy
                Rule as protected health information or PHI). The Privacy Rule sets forth guidelines intended to preserve the integrity and confidentiality of
                PHI. The Privacy Rule applies to health plans, health care clearinghouses and health care providers. The Privacy Rule can be found at 45 CFR,
                Part 164, Subpart E or at{' '}
                <a href="http://aspe.hhs.gov/admnsimp/final/pvctxt01.htm" rel="noopener noreferrer" target="_blank">
                  http://aspe.hhs.gov/admnsimp/final/pvctxt01.htm
                </a>
                .
              </p>
              <p>
                Your acceptance of this agreement acknowledges that you will follow The Privacy Rule as it relates to any PHI to which you have access through
                any CompuMed, Inc. Imaging products. Also, it acknowledges your understanding that the login and access code provided to you should never be
                shared with anyone and that upon completion of using this image viewing application, you will log out of the system so that accidental access to
                PHI is not granted.
              </p>
            </StyledCard>

            <StyledCard>
              <h2>Access Share</h2>
              <hr />
              <StyledInputsContainer>
                {share.hasPassword && (
                  <InputField name="accessCode" type="password" required autoComplete={`share-access-code-${linkId}`} label="Access Code" />
                )}

                {/* <InputField name="email" type="email" label="Email Address (optional)" autoComplete="email" /> */}

                <CheckboxField name="consent" inline required label="I have read and agree to the HIPAA agreement." validator={consentValidator} />

                <StyledSubmitButtonContainer>
                  <Button type="submit">Access Share</Button>
                </StyledSubmitButtonContainer>
              </StyledInputsContainer>
            </StyledCard>

            {errors.length > 0 && (
              <div>
                {errors.map((e) => (
                  <MessageBox key={e} variant="error" message={e} />
                ))}
              </div>
            )}
          </StyledForm>
          <StyledFooterDiv>
            <p>
              For support, please email <a href="mailto:opo@compumedinc.com">opo@compumedinc.com</a> or call <a href="tel:3102585055">(310) 258-5055</a>.
            </p>
            <p>© Copyright 2023 - 2025 CompuMed, Inc. - All rights reserved</p>
          </StyledFooterDiv>
        </StyledPageContent>
      </FormProvider>
    </Page>
  );
});

ShareLanding.displayName = 'ShareLanding';

const StyledPageContent = styled.div`
  grid-template-rows: min-content 1fr min-content;
`;

const StyledShareHeader = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 43px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.palette.white};
  padding: 0 12px;
  column-gap: 12px;
  overflow: hidden;

  ${BreakpointSelectors.Mobile} & {
    flex-direction: row-reverse;
  }
`;

const StyledLogoContainer = styled.div`
  flex: 0 0 auto;
`;

const StyledForm = styled.form`
  display: grid;
  row-gap: 16px;
  padding-top: 13px;
  grid-auto-rows: min-content;

  ${BreakpointSelectors.Desktop} & {
    justify-content: center;
    align-items: start;
    grid-template-columns: 500px;
  }

  ${BreakpointSelectors.Mobile} & {
    grid-template-columns: 1fr;
  }
`;

const StyledCard = styled(Card)`
  padding: 0 0 12px 0;

  ${BreakpointSelectors.Mobile} & {
    border-radius: 0;
  }

  h2 {
    margin: 0;
    font-size: 20px;
    line-height: 32px;
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};
    user-select: none;
    padding: 6px 9px;
  }

  hr {
    background-color: ${({ theme }) => theme.colors.palette.grayscale[5]};
    margin: 0 0 9px 0;
  }

  p {
    margin-bottom: 0;
    padding: 0 9px;
  }
  p + p {
    margin-top: 0.5em;
  }
`;

const StyledInfoList = styled.div`
  flex: 1 1 0;
  display: flex;
  column-gap: ${({ theme }) => theme.space.spacing60};
  overflow: hidden;
  flex-wrap: wrap;
  height: 100%;

  ${BreakpointSelectors.Mobile} & {
    justify-content: end;
  }
`;

const StyledInfoContainer = styled.div`
  min-width: 150px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${BreakpointSelectors.Mobile} & {
    flex: 0 1 150px;
  }
`;

const StyledInfoLabel = styled.div`
  text-align: left;
  white-space: nowrap;
  min-height: 1lh;
`;

const StyledInfoValue = styled.div`
  text-align: left;
  white-space: nowrap;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  min-height: 1lh;
`;

const StyledInputsContainer = styled.div`
  padding: 6px 9px 0 9px;
  display: grid;
  row-gap: 8px;
`;

const StyledSubmitButtonContainer = styled.div`
  padding: 6px 9px;
`;

const StyledFooterDiv = styled.div`
  align-self: center;
  color: ${({ theme }) => theme.colors.palette.grayscale[7]};
  user-select: none;
  text-align: center;

  p {
    text-wrap: balance;
    margin: 0;
  }
`;

const StyledErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: ${({ theme }) => theme.space.spacing40};
`;

const StyledErrorCard = styled(Card)`
  max-width: 600px;
  text-align: center;
  padding: ${({ theme }) => theme.space.spacing30};

  h2 {
    color: ${({ theme }) => theme.colors.error};
    margin-bottom: ${({ theme }) => theme.space.spacing20};
  }

  p {
    margin-bottom: ${({ theme }) => theme.space.spacing20};
    line-height: 1.6;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const StyledErrorLogoContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.space.spacing20};
  background-color: ${({ theme }) => theme.colors.palette.blues[5]};
  padding: 1em;
  img {
    height: 32px;
    width: auto;
    max-width: 200px;
  }
`;
